import {useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';

import {getLcdSubscriptionsTrees} from 'src/openapi/admin/apis/LcdSubscriptionsDictionaryApi';
import {useAppDispatch} from 'src/store/hooks';

import {selectLcdSubscriptionTrees} from './services/dictionary-slice';

import type {DictionaryTree} from 'src/openapi/admin/models/DictionaryTree';
import type {RegionSettings} from 'src/openapi/admin/models/RegionSettings';

let isRequestPending = false;

const collectLabels = (nodes: DictionaryTree[]): Record<string, string> => {
    const labels: Record<string, string> = {};
    for (const node of nodes) {
        labels[node.id] = node.description;
        if (node.children?.length) {
            Object.assign(labels, collectLabels(node.children));
        }
    }
    return labels;
};

// Tree hierarchy: productType -> region -> firstLevelItem -> leaf.
// productTypeId -> leafId -> firstLevelDescription
const buildLeafToFirstLevelMap = (
    trees: DictionaryTree[],
): Record<string, Record<string, string>> => {
    const result: Record<string, Record<string, string>> = {};
    for (const productType of trees) {
        result[productType.id] = {};
        const assignLeaves = (node: DictionaryTree, label: string) => {
            if (!node.children?.length) {
                result[productType.id][node.id] = label;
            } else {
                for (const child of node.children) {
                    assignLeaves(child, label);
                }
            }
        };
        for (const region of productType.children || []) {
            for (const firstLevelItem of region.children || []) {
                assignLeaves(firstLevelItem, firstLevelItem.description);
            }
        }
    }
    return result;
};

// regionId -> display name (e.g. US -> United States)
const buildRegionLabels = (trees: DictionaryTree[]): Record<string, string> => {
    const labels: Record<string, string> = {};
    for (const productType of trees) {
        for (const region of productType.children || []) {
            labels[region.id] = region.description;
        }
    }
    return labels;
};

// productTypeId -> first-level descriptions in tree order
const buildFirstLevelOrder = (trees: DictionaryTree[]): Record<string, string[]> => {
    const result: Record<string, string[]> = {};
    for (const productType of trees) {
        const labels: string[] = [];
        for (const region of productType.children || []) {
            for (const firstLevelItem of region.children || []) {
                if (!labels.includes(firstLevelItem.description)) {
                    labels.push(firstLevelItem.description);
                }
            }
        }
        result[productType.id] = labels;
    }
    return result;
};

export type LcdRegionSection = {
    region: string,
    firstLevelLabels: string[],
};

type LcdDictionaryMaps = {
    trees: DictionaryTree[],
    leafToFirstLevel: Record<string, Record<string, string>>,
    regionLabels: Record<string, string>,
    firstLevelOrder: Record<string, string[]>,
};

// userId -> productTypeId -> region settings
export const buildLcdRegionSections = (
    data: Record<string, Array<RegionSettings>> | undefined,
    productTypeId: string,
    {trees, leafToFirstLevel, regionLabels, firstLevelOrder: orders}: LcdDictionaryMaps,
): LcdRegionSection[] | null => {
    if (!data) {
        return null;
    }
    const productType = trees.find(tree => tree.id === productTypeId);
    if (!productType) {
        return null;
    }
    const regionSettings = data[productType.id] || [];
    const firstLevelMap = leafToFirstLevel[productType.id] || {};
    const firstLevelOrder = orders[productType.id] || [];
    return regionSettings
        .filter(({region}) => productType.children?.some(({id}) => id === region))
        .map(regionSetting => {
            const seen = new Set<string>();
            const firstLevelLabels: string[] = [];
            for (const leafId of regionSetting.subscriptions) {
                const label = firstLevelMap[leafId];
                if (label && !seen.has(label)) {
                    seen.add(label);
                    firstLevelLabels.push(label);
                }
            }
            firstLevelLabels.sort(
                (a, b) => firstLevelOrder.indexOf(a) - firstLevelOrder.indexOf(b),
            );
            return {
                region: regionLabels[regionSetting.region] || regionSetting.region,
                firstLevelLabels,
            };
        })
        .filter(r => r.firstLevelLabels.length > 0)
        .sort((a, b) => b.region.localeCompare(a.region));
};

export const useLcdSubscriptionDictionary = () => {
    const apiDispatch = useAppDispatch();
    const lcdSubscriptionTrees = useSelector(selectLcdSubscriptionTrees);

    useEffect(() => {
        if (lcdSubscriptionTrees.length) {
            isRequestPending = false;
            return;
        }
        if (!isRequestPending) {
            isRequestPending = true;
            const resetRequestPending = () => {
                isRequestPending = false;
            };
            apiDispatch(getLcdSubscriptionsTrees()).then(resetRequestPending, resetRequestPending);
        }
    }, [lcdSubscriptionTrees.length, apiDispatch]);

    const lcdLeafLabels = useMemo(
        () => collectLabels(lcdSubscriptionTrees),
        [lcdSubscriptionTrees],
    );

    const lcdLeafToFirstLevel = useMemo(
        () => buildLeafToFirstLevelMap(lcdSubscriptionTrees),
        [lcdSubscriptionTrees],
    );

    const lcdRegionLabels = useMemo(
        () => buildRegionLabels(lcdSubscriptionTrees),
        [lcdSubscriptionTrees],
    );

    const lcdFirstLevelOrder = useMemo(
        () => buildFirstLevelOrder(lcdSubscriptionTrees),
        [lcdSubscriptionTrees],
    );

    return {
        lcdSubscriptionTrees,
        lcdLeafLabels,
        lcdLeafToFirstLevel,
        lcdRegionLabels,
        lcdFirstLevelOrder,
    };
};
